// Persistent UI snapshots (localStorage) — stale-while-revalidate for the data tiles.
// Each tile paints its last-known list instantly on open (or a shimmer skeleton on a
// true first run) while the network revalidates in the background. One paired account
// per device, so snapshots are keyed per module only; unlink / re-pair clears them all.
// Entries expire after 7 days so long-dead data falls back to a clean first load.
const PREFIX = "rscan-snap:1:";
const MAX_AGE_MS = 7 * 86_400_000;

export function loadSnap<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const { t, d } = JSON.parse(raw) as { t: number; d: T };
    if (typeof t !== "number" || Date.now() - t > MAX_AGE_MS) return null;
    return d ?? null;
  } catch {
    return null;
  }
}

export function saveSnap(key: string, data: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ t: Date.now(), d: data }));
  } catch {
    /* private mode / quota — snapshots are best-effort */
  }
  // Anyone painting derived UI (Home tile badges, cross-module search) refreshes here —
  // every module saves its list right after a successful fetch, so this IS "data changed".
  document.dispatchEvent(new CustomEvent("rp:snap", { detail: key }));
}

/** Unlink / fresh pairing: drop every snapshot so no data crosses accounts. */
export function clearSnaps(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* private mode */
  }
}

/** THE LOADING CANON — the same three shapes the pane and the PWA use (taskpane/src/ui/Skeleton.tsx):
 *  a list, a form, or a block of prose. Never the word "Loading": a skeleton shows the SHAPE of what
 *  is coming, so the eye already knows where to look. Change a shape here, change it there too. */
export function skeletonHtml(rows = 5): string {
  let out = "";
  for (let i = 0; i < rows; i++) {
    out +=
      `<div class="skel-row">` +
      `<span class="skel skel-av"></span>` +
      `<span class="skel-body"><span class="skel" style="width:${62 - (i % 3) * 9}%"></span>` +
      `<span class="skel" style="width:${34 + (i % 4) * 7}%"></span></span>` +
      `</div>`;
  }
  return out;
}

/** A record being edited: a label stub above each field stub, so nothing jumps when the real
 *  fields arrive. Mirrors <SkForm/>. */
export function skeletonFormHtml(fields = 4, title = true): string {
  let out = `<div class="skel-form">`;
  if (title) out += `<span class="skel skel-h"></span>`;
  for (let i = 0; i < fields; i++) {
    out +=
      `<span class="skel-fld">` +
      `<span class="skel skel-lab" style="width:${28 + (i % 3) * 8}%"></span>` +
      `<span class="skel skel-in"></span>` +
      `</span>`;
  }
  return out + `</div>`;
}

/** A paragraph: full-width lines with a short last one, the way real text ends. Mirrors <SkText/>. */
export function skeletonTextHtml(lines = 4): string {
  let out = `<div class="skel-text">`;
  for (let i = 0; i < lines; i++) {
    const w = i === lines - 1 ? 46 : 88 + (i % 3) * 4;
    out += `<span class="skel" style="width:${w}%"></span>`;
  }
  return out + `</div>`;
}
